'use client'

import classes from '../../error-page.module.css'

import { Button } from '@mantine/core'
import { signOut } from 'aws-amplify/auth'
import { CircleQuestionMark, Home, LogIn, LogOut, RefreshCw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import { useAmplitude } from '@/components/providers/amplitude'
import { routes } from '@/constants/routes'
import { ZENDESK_SUPPORT_ROUTE } from '@/constants/zendesk'
import { logoutAction } from '@/lib/auth/auth.actions'
import { clearReturnUrl, rememberCurrentReturnUrl } from '@/lib/auth/return-url'

const ICON_PROPS = { nonScalingStroke: true, size: 12, strokeWidth: 1 }

/** Opens Zendesk support in a new tab. */
export const SupportAction = () => {
  const t = useTranslations('common')
  const { trackSupportClicked } = useAmplitude()

  return (
    <Button
      className={classes.action}
      component='a'
      href={ZENDESK_SUPPORT_ROUTE}
      leftSection={<CircleQuestionMark {...ICON_PROPS} />}
      onClick={() => trackSupportClicked()}
      rel='noopener noreferrer'
      size='xs'
      target='_blank'
      variant='filled'
    >
      {t('button-support')}
    </Button>
  )
}

/** Logs out without remembering this page, so the next login does not land here again. */
export const LogoutAction = () => {
  const t = useTranslations('common')

  const handleLogout = async () => {
    clearReturnUrl()
    await logoutAction()
  }

  return (
    <Button
      className={classes.action}
      leftSection={<LogOut {...ICON_PROPS} />}
      onClick={handleLogout}
      size='xs'
      variant='filled'
    >
      {t('button-logout')}
    </Button>
  )
}

/** Ends the stale session and sends the user to login, then back to this page. */
export const LoginAction = () => {
  const t = useTranslations('errors')
  const router = useRouter()

  const handleLogin = async () => {
    rememberCurrentReturnUrl()
    await signOut()
    await logoutAction()
    router.push(routes.login)
  }

  return (
    <Button
      className={classes.action}
      leftSection={<LogIn {...ICON_PROPS} />}
      onClick={handleLogin}
      size='xs'
      variant='filled'
    >
      {t('button-login')}
    </Button>
  )
}

export const HomeAction = () => {
  const t = useTranslations('errors')

  return (
    <Button
      className={classes.action}
      component={Link}
      href={routes.index}
      leftSection={<Home {...ICON_PROPS} />}
      size='xs'
      variant='filled'
    >
      {t('button-home')}
    </Button>
  )
}

type RetryActionProps = {
  onClick: () => void
}

export const RetryAction = (props: RetryActionProps) => {
  const { onClick } = props
  const t = useTranslations('errors')

  return (
    <Button
      className={classes.action}
      leftSection={<RefreshCw {...ICON_PROPS} />}
      onClick={onClick}
      size='xs'
      variant='filled'
    >
      {t('button-try-again')}
    </Button>
  )
}
